import { useState, useRef, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError, API_BASE } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Search, Loader2, CheckCircle2, Package, Upload, X, ArrowRight, ArrowLeft, Image as ImageIcon, Banknote, Repeat, Ticket,
} from "lucide-react";
import { toast } from "sonner";

const STEPS = ["Comandă", "Produse", "Detalii", "Rambursare", "Confirmare"];

export default function PublicReturnFormPage() {
  const [params] = useSearchParams();
  const embed = params.get("embed") === "1";

  // Embed mode: force light theme, hide chrome, transparent background.
  // IMPORTANT layout fix (2026-02-16 v3): pe mobil, când iframe-ul are înălțime
  // fixă (cum face Shopify de obicei), conținutul nostru depășea viewport-ul
  // iframe-ului iar butonul "Continuă" ajungea sub footer-ul magazinului. Fix:
  //   • html/body/#root: 100dvh + overflow:hidden (dynamic viewport height,
  //     corectează problema iOS Safari unde 100vh include adressbar-ul)
  //   • Page wrapper: flex column h-dvh
  //   • Stepper: flex-shrink-0
  //   • Content area: flex-1 min-h-0 overflow-y-auto (scroll intern în iframe)
  //   • Action bar: flex-shrink-0 fix la jos cu safe-area-inset-bottom
  // Astfel butonul "Înapoi/Continuă" rămâne SIEMPRE vizibil indiferent de
  // înălțimea iframe-ului sau de cantitatea de conținut din pas.
  useEffect(() => {
    if (!embed) return;
    document.documentElement.classList.remove("dark");
    document.documentElement.classList.add("embed-mode");
    document.body.classList.add("embed-mode");
    const styleId = "ieftinit-embed-overrides";
    if (!document.getElementById(styleId)) {
      const s = document.createElement("style");
      s.id = styleId;
      s.textContent = `
        html.embed-mode, body.embed-mode {
          background: transparent !important;
          height: 100vh !important;
          height: 100dvh !important;
          margin: 0 !important;
          padding: 0 !important;
          overflow: hidden !important;
        }
        body.embed-mode > #root {
          height: 100vh !important;
          height: 100dvh !important;
          overflow: hidden !important;
          display: flex !important;
          flex-direction: column !important;
        }
        body.embed-mode > #root > * {
          flex: 1 1 auto;
          min-height: 0;
        }
        html.embed-mode { color-scheme: light !important; }
        /* Hide Emergent platform watermark / branding badge */
        [class*="emergent-badge"],
        [id*="emergent-badge"],
        a[href*="emergent.com"][target="_blank"],
        a[href*="emergent.dev"][target="_blank"],
        a[href*="emergent.sh"][target="_blank"],
        a[href*="Made with Emergent"],
        iframe[src*="emergent.com"]:not([src*="/retur"]) {
          display: none !important; visibility: hidden !important;
        }
      `;
      document.head.appendChild(s);
    }
    return () => {
      document.documentElement.classList.remove("embed-mode");
      document.body.classList.remove("embed-mode");
    };
  }, [embed]);

  // Each time the user advances to a new step, ask the parent to scroll the
  // iframe back into view at the top. This guarantees the new step's title
  // is visible even after the parent grew/shrunk on step transitions.
  // (defined later, after `step` state is declared)

  const [step, setStep] = useState(0);
  const [orderNumber, setOrderNumber] = useState("");
  const [identity, setIdentity] = useState(""); // email or phone
  const [order, setOrder] = useState(null);
  const [loadingOrder, setLoadingOrder] = useState(false);
  const [selectedItems, setSelectedItems] = useState({}); // {line_item_id: {qty, reason}}
  const [form, setForm] = useState({
    customer_name: "", email: "", phone: "",
    reason: "", notes: "",
    return_type: "refund", iban: "", bank: "",
    accepted_terms: false,
  });
  const [files, setFiles] = useState([]); // [{id, filename, ...}]
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const fileRef = useRef();

  // Scroll content area to top on step change (sticky action bar nu se mută,
  // dar conținutul de pas trebuie resetat la sus pentru claritate).
  const contentRef = useRef(null);
  useEffect(() => {
    contentRef.current?.scrollTo?.({ top: 0, behavior: "instant" });
    if (embed) {
      try {
        window.parent?.postMessage(
          { type: "ieftinit:scrollTop", source: "ieftinit-retur" },
          "*"
        );
      } catch (_) { /* noop */ }
    }
  }, [embed, step]);

  const lookup = async (e) => {
    e?.preventDefault?.();
    if (!orderNumber.trim()) return;
    setLoadingOrder(true);
    try {
      const id = identity.trim();
      const params = {};
      if (id) {
        if (id.includes("@")) params.email = id;
        else params.phone = id;
      }
      const { data } = await api.get(`/public/order/${orderNumber.trim().replace(/^#/, "")}`, { params });
      setOrder(data);
      setForm((f) => ({
        ...f,
        customer_name: f.customer_name || `${data.customer?.first_name || ""} ${data.customer?.last_name || ""}`.trim(),
        email: f.email || data.email || (id.includes("@") ? id : ""),
        phone: f.phone || (!id.includes("@") ? id : ""),
      }));
      const initial = {};
      data.line_items.forEach((li) => { initial[li.id] = { qty: 0, reason: "", item: li }; });
      setSelectedItems(initial);
      setStep(1);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setLoadingOrder(false); }
  };

  const toggleQty = (lineId, delta, max) => {
    setSelectedItems((s) => {
      const cur = s[lineId];
      const next = Math.max(0, Math.min(max, cur.qty + delta));
      return { ...s, [lineId]: { ...cur, qty: next } };
    });
  };

  const selectedCount = Object.values(selectedItems).reduce((a, b) => a + b.qty, 0);

  const upload = async (e) => {
    const fs = Array.from(e.target.files || []);
    if (!fs.length) return;
    setUploading(true);
    try {
      for (const file of fs) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/public/upload", fd, { headers: { "Content-Type": undefined } });
        setFiles((cur) => [...cur, data]);
      }
      toast.success(`${fs.length} fișier(e) încărcat(e)`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const submit = async () => {
    if (!form.accepted_terms) return toast.error("Acceptă termenii");
    if (form.return_type === "refund" && !form.iban) return toast.error("IBAN obligatoriu pentru rambursare");
    setSubmitting(true);
    const items = Object.values(selectedItems).filter((s) => s.qty > 0).map((s) => ({
      line_item_id: s.item.id,
      product_id: s.item.product_id,
      title: s.item.title,
      variant_title: s.item.variant_title,
      quantity: s.qty,
      price: s.item.price,
      image: s.item.image,
      reason: s.reason,
    }));
    if (items.length === 0) { setSubmitting(false); return toast.error("Selectează cel puțin un produs"); }

    try {
      const { data } = await api.post("/public/returns", {
        order_number: order.order_number,
        customer_name: form.customer_name,
        email: form.email,
        phone: form.phone,
        items,
        reason: form.reason,
        notes: form.notes,
        return_type: form.return_type,
        iban: form.iban,
        bank: form.bank,
        file_ids: files.map((f) => f.id),
        accepted_terms: form.accepted_terms,
      });
      setSubmitted(data);
      setStep(4);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSubmitting(false); }
  };

  const canProceed = () => {
    if (step === 0) return !!order;
    if (step === 1) return selectedCount > 0;
    if (step === 2) return form.customer_name && form.email && form.phone && form.reason;
    if (step === 3) return form.accepted_terms && (form.return_type !== "refund" || form.iban);
    return true;
  };

  if (submitted) {
    return (
      <div className={`${embed ? "" : "min-h-screen"} bg-background text-foreground flex items-center justify-center ${embed ? "p-4" : "p-6"}`}>
        <Card className="max-w-md w-full p-10 text-center bg-card border-border" data-testid="success-card">
          <div className="h-14 w-14 rounded-xl bg-emerald-500/10 mx-auto flex items-center justify-center mb-6">
            <CheckCircle2 className="h-7 w-7 text-emerald-500" />
          </div>
          <h2 className="font-display text-2xl font-bold tracking-tight mb-2">Cerere trimisă!</h2>
          <p className="text-muted-foreground text-sm leading-relaxed mb-6">
            Mulțumim! Echipa noastră va analiza cererea ta în maxim 24h. Vei primi un email de confirmare la <strong>{form.email}</strong>.
          </p>
          <div className="bg-secondary rounded-lg p-4 mb-6">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">Numărul cererii tale</div>
            <div className="font-mono text-lg font-bold">{submitted.return_number}</div>
          </div>
          <Button variant="outline" onClick={() => window.location.reload()}>Trimite altă cerere</Button>
        </Card>
      </div>
    );
  }

  return (
    <div
      className={`ieftinit-retur-root flex flex-col ${embed ? "h-screen" : "min-h-screen"} ${embed ? "bg-transparent" : "bg-background"} text-foreground`}
      style={embed ? { height: "100dvh" } : undefined}
    >
      {/* Header - hidden in embed mode */}
      {!embed && (
        <header className="border-b border-border bg-white flex-shrink-0">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src="/ieftinit-logo.png" alt="IEFTINIT.RO" className="h-10 w-auto object-contain" />
              <div className="leading-tight">
                <div className="font-display font-black text-lg">IEFTINIT.RO</div>
                <div className="text-[10px] text-muted-foreground uppercase tracking-widest font-semibold">Formular retur</div>
              </div>
            </div>
          </div>
        </header>
      )}

      {/* Stepper — flex-shrink-0 */}
      <div className={`flex-shrink-0 max-w-3xl mx-auto w-full px-4 sm:px-6 ${embed ? "pt-3 pb-2" : "pt-6 sm:pt-10 pb-4"}`}>
        <div className="flex items-center justify-between">
          {STEPS.map((s, i) => (
            <div key={s} className="flex-1 flex items-center">
              <div className={`flex flex-col items-center gap-1.5 ${i <= step ? "text-foreground" : "text-muted-foreground/60"}`}>
                <div className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-semibold transition-colors ${
                  i < step ? "bg-primary text-primary-foreground" : i === step ? "bg-foreground text-background" : "bg-secondary"
                }`}>
                  {i < step ? <CheckCircle2 className="h-4 w-4" /> : i + 1}
                </div>
                <div className="text-[10px] uppercase tracking-widest font-semibold hidden sm:block">{s}</div>
              </div>
              {i < STEPS.length - 1 && <div className={`flex-1 h-px mx-2 ${i < step ? "bg-primary" : "bg-border"}`} />}
            </div>
          ))}
        </div>
      </div>

      {/* Scrollable content area — flex-1, scroll intern, NU body scroll */}
      <div
        ref={contentRef}
        className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden"
        style={{ WebkitOverflowScrolling: "touch" }}
      >
        <div className={`max-w-3xl mx-auto px-4 sm:px-6 pb-6 ${embed ? "pt-2" : "pt-2"}`}>

        {/* STEP 0: order lookup */}
        {step === 0 && (
          <Card className="p-6 sm:p-10 bg-card border-border" data-testid="step-order">
            <h1 className="font-display text-3xl font-bold tracking-tight mb-2">Hai să găsim comanda ta.</h1>
            <p className="text-muted-foreground text-sm mb-8 leading-relaxed">
              Introdu numărul comenzii pe care vrei să o returnezi. Îl găsești în emailul de confirmare.
            </p>
            <form onSubmit={lookup} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="order">Număr comandă *</Label>
                <div className="relative">
                  <Input
                    id="order"
                    value={orderNumber}
                    onChange={(e) => setOrderNumber(e.target.value)}
                    placeholder="ex: 1001"
                    className="h-12 text-lg pr-12"
                    data-testid="order-number-input"
                  />
                  <Search className="absolute right-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="identity">Email sau telefon (pentru validare)</Label>
                <Input
                  id="identity"
                  value={identity}
                  onChange={(e) => setIdentity(e.target.value)}
                  placeholder="ex: nume@exemplu.ro sau +40 7XX XXX XXX"
                  className="h-12"
                  data-testid="identity-input"
                />
                <p className="text-xs text-muted-foreground">Folosim aceste date pentru a confirma că ești proprietarul comenzii.</p>
              </div>
              <Button type="submit" className="w-full h-12" disabled={loadingOrder || !orderNumber.trim()} data-testid="lookup-order-btn">
                {loadingOrder ? <Loader2 className="h-4 w-4 animate-spin" /> : (<>Caută comanda <ArrowRight className="h-4 w-4 ml-2" /></>)}
              </Button>
            </form>
          </Card>
        )}

        {/* STEP 1: products */}
        {step === 1 && order && (
          <Card className="p-6 sm:p-10 bg-card border-border" data-testid="step-products">
            <h1 className="font-display text-3xl font-bold tracking-tight mb-2">Ce vrei să returnezi?</h1>
            <p className="text-muted-foreground text-sm mb-6">Selectează cantitatea pentru fiecare produs din comanda <strong>{order.name}</strong>.</p>
            <div className="space-y-3">
              {order.line_items.map((li) => {
                const sel = selectedItems[li.id];
                return (
                  <div key={li.id} className="flex items-center gap-4 p-3 border border-border rounded-lg" data-testid={`item-${li.id}`}>
                    {li.image && <img src={li.image} alt={li.title} className="h-14 w-14 rounded-md object-cover bg-secondary" />}
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm">{li.title}</div>
                      <div className="text-xs text-muted-foreground">{li.variant_title}</div>
                      <div className="text-xs text-muted-foreground font-mono mt-1">Disponibil: {li.quantity}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button type="button" variant="outline" size="icon" className="h-8 w-8" onClick={() => toggleQty(li.id, -1, li.quantity)} disabled={!sel?.qty} data-testid={`qty-minus-${li.id}`}>−</Button>
                      <div className="font-mono text-base font-semibold w-6 text-center tabular-nums">{sel?.qty || 0}</div>
                      <Button type="button" variant="outline" size="icon" className="h-8 w-8" onClick={() => toggleQty(li.id, 1, li.quantity)} disabled={sel?.qty >= li.quantity} data-testid={`qty-plus-${li.id}`}>+</Button>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex justify-between items-center mt-6 pt-6 border-t border-border">
              <Button variant="ghost" onClick={() => setStep(0)}><ArrowLeft className="h-4 w-4 mr-1" /> Înapoi</Button>
              <Button onClick={() => setStep(2)} disabled={!canProceed()} data-testid="step1-next">Continuă ({selectedCount}) <ArrowRight className="h-4 w-4 ml-1" /></Button>
            </div>
          </Card>
        )}

        {/* STEP 2: details */}
        {step === 2 && (
          <Card className="p-6 sm:p-10 bg-card border-border" data-testid="step-details">
            <h1 className="font-display text-3xl font-bold tracking-tight mb-2">Date și motiv</h1>
            <p className="text-muted-foreground text-sm mb-6">Spune-ne cum te putem contacta și de ce returnezi.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Nume complet *</Label><Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} className="h-11" data-testid="customer-name" /></div>
              <div className="space-y-2"><Label>Telefon *</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="h-11" placeholder="+40 7XX XXX XXX" data-testid="customer-phone" /></div>
              <div className="space-y-2 sm:col-span-2"><Label>Email *</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-11" data-testid="customer-email" /></div>
              <div className="space-y-2 sm:col-span-2"><Label>Motivul returnării *</Label><Textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="ex: Mărimea nu mi se potrivește" rows={3} data-testid="reason" /></div>
              <div className="space-y-2 sm:col-span-2"><Label>Observații suplimentare</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} data-testid="notes" /></div>
              <div className="sm:col-span-2 space-y-2">
                <Label>Fotografii / video (opțional)</Label>
                <div className="border-2 border-dashed border-border rounded-lg p-4 text-center bg-secondary/30">
                  <Upload className="h-5 w-5 text-muted-foreground mx-auto mb-2" />
                  <input ref={fileRef} type="file" multiple accept="image/*,video/*" onChange={upload} className="hidden" id="file-input" data-testid="file-input" />
                  <label htmlFor="file-input" className="text-sm text-primary cursor-pointer">{uploading ? "Se încarcă…" : "Selectează fișiere"}</label>
                  <p className="text-xs text-muted-foreground mt-1">Max 50MB · jpg, png, webp, mp4</p>
                </div>
                {files.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {files.map((f) => (
                      <div key={f.id} className="relative h-16 w-16 rounded-md overflow-hidden border border-border bg-secondary group">
                        {f.content_type?.startsWith("image/") ? (
                          <img src={`${API_BASE}/files/${f.id}`} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="h-full w-full flex items-center justify-center text-muted-foreground"><ImageIcon className="h-5 w-5" /></div>
                        )}
                        <button onClick={() => setFiles((fs) => fs.filter((x) => x.id !== f.id))} className="absolute top-0.5 right-0.5 h-5 w-5 rounded-full bg-background/80 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </Card>
        )}

        {/* STEP 3: refund */}
        {step === 3 && (
          <Card className="p-6 sm:p-10 bg-card border-border" data-testid="step-refund">
            <h1 className="font-display text-3xl font-bold tracking-tight mb-2">Cum vrei să primești banii?</h1>
            <p className="text-muted-foreground text-sm mb-6">Alege opțiunea preferată.</p>
            <RadioGroup value={form.return_type} onValueChange={(v) => setForm({ ...form, return_type: v })} className="space-y-2 mb-6">
              {[
                { value: "refund", icon: Banknote, label: "Rambursare bani", desc: "Banii întorși în IBAN-ul tău" },
                { value: "exchange", icon: Repeat, label: "Schimb produs", desc: "Înlocuim produsul cu altă mărime/variantă" },
                { value: "voucher", icon: Ticket, label: "Voucher", desc: "Voucher pentru următoarea comandă" },
              ].map((opt) => (
                <label key={opt.value} className={`flex items-center gap-4 p-4 border rounded-lg cursor-pointer transition-colors ${form.return_type === opt.value ? "border-primary bg-primary/5" : "border-border hover:bg-secondary/40"}`} data-testid={`type-${opt.value}`}>
                  <RadioGroupItem value={opt.value} />
                  <opt.icon className="h-5 w-5 text-muted-foreground" />
                  <div className="flex-1">
                    <div className="font-medium text-sm">{opt.label}</div>
                    <div className="text-xs text-muted-foreground">{opt.desc}</div>
                  </div>
                </label>
              ))}
            </RadioGroup>

            {form.return_type === "refund" && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6 p-4 bg-secondary/30 rounded-lg">
                <div className="space-y-2 sm:col-span-2"><Label>IBAN *</Label><Input value={form.iban} onChange={(e) => setForm({ ...form, iban: e.target.value.toUpperCase() })} className="h-11 font-mono" placeholder="RO49AAAA1B31007593840000" data-testid="iban" /></div>
                <div className="space-y-2 sm:col-span-2"><Label>Banca</Label><Input value={form.bank} onChange={(e) => setForm({ ...form, bank: e.target.value })} className="h-11" placeholder="ex: BCR, BT, ING…" data-testid="bank" /></div>
              </div>
            )}

            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox checked={form.accepted_terms} onCheckedChange={(v) => setForm({ ...form, accepted_terms: !!v })} data-testid="accept-terms" />
              <span className="text-xs text-muted-foreground leading-relaxed">
                Sunt de acord cu termenii și condițiile de retur ale IEFTINIT.RO și înțeleg că produsul trebuie returnat în stare originală în maxim 14 zile.
              </span>
            </label>
          </Card>
        )}
        </div>
      </div>

      {/* Action bar — flex-shrink-0, lipit jos pe iframe. Pe mobil, NU se mai
          poate ascunde sub footer pentru că face parte din viewport flex layout. */}
      {step !== 0 && step !== 4 && (
        <div
          className="flex-shrink-0 border-t border-border bg-background"
          style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
        >
          <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 flex justify-between items-center gap-3">
            <Button
              variant="ghost"
              onClick={() => setStep(step - 1)}
              data-testid="nav-back"
              className="h-11"
            >
              <ArrowLeft className="h-4 w-4 mr-1" /> Înapoi
            </Button>
            {step === 1 && (
              <Button
                onClick={() => setStep(2)}
                disabled={!canProceed()}
                data-testid="step1-next"
                className="h-11 px-6"
              >
                Continuă ({selectedCount}) <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            )}
            {step === 2 && (
              <Button
                onClick={() => setStep(3)}
                disabled={!canProceed()}
                data-testid="step2-next"
                className="h-11 px-6"
              >
                Continuă <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            )}
            {step === 3 && (
              <Button
                onClick={submit}
                disabled={!canProceed() || submitting}
                data-testid="submit-return"
                className="h-11 px-6"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Trimite cererea <ArrowRight className="h-4 w-4 ml-1" /></>}
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
